import { faClose } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, ButtonProps } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export interface ModalProps {
  open?: boolean;
  defaultOpen?: boolean;
  maskClosable?: boolean;
  mask?: boolean;
  closable?: boolean;
  closeIcon?: React.ReactNode;
  title?: React.ReactNode;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  width?: number;
  height?: number;
  style?: React.CSSProperties;
  bodyStyle?: React.CSSProperties;
  className?: string;
  wrapClassName?: string;
  zIndex?: number;
  centered?: boolean;
  keyboard?: boolean;
  destroyOnClose?: boolean;
  onCancel?: () => void;
  onOk?: () => void;
  afterClose?: () => void;
  getContainer?: HTMLElement | (() => HTMLElement) | false;
  focusTriggerAfterClose?: boolean;
  forceRender?: boolean;
  okText?: React.ReactNode;
  okType?: 'primary' | 'dashed' | 'link' | 'text' | 'default';
  cancelText?: React.ReactNode;
  okButtonProps?: Omit<ButtonProps, 'type'>;
  cancelButtonProps?: Omit<ButtonProps, 'type'>;
  confirmLoading?: boolean;
  draggable?: boolean;
  /** When true (default), the user can resize the modal by dragging its corner */
  resizable?: boolean;
  onResizeStop?: () => void;
  onDragStop?: () => void;
  onResize?: () => void;
  onDrag?: () => void;
}

const Modal: React.FC<ModalProps> = ({
  open = false,
  defaultOpen = false,
  maskClosable = true,
  mask = true,
  closable = true,
  closeIcon = <FontAwesomeIcon icon={faClose} />,
  title,
  children,
  footer,
  width,
  height,
  style,
  bodyStyle,
  className = '',
  wrapClassName = '',
  zIndex = 1000,
  centered = true,
  keyboard = true,
  destroyOnClose: _destroyOnClose = false,
  onCancel,
  onOk,
  getContainer = document.body,
  focusTriggerAfterClose: _focusTriggerAfterClose = true,
  forceRender = false,
  draggable = true,
  resizable = true,
  okText = 'OK',
  okType = 'primary',
  cancelText = 'Cancel',
  okButtonProps,
  cancelButtonProps,
  confirmLoading = false,
  onDragStop,
  onDrag,
}) => {
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(defaultOpen);

  // ─── Drag state ────────────────────────────────────────────────────────
  const [isDragging, setIsDragging] = useState(false);
  const [dragPos, setDragPos] = useState<{ x: number; y: number } | null>(null);
  const dragStart = useRef<{
    mouseX: number;
    mouseY: number;
    boxX: number;
    boxY: number;
  } | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  // ─── Resize state (JS-based: CSS resize:both fails in flex containers) ─
  const [resizeSize, setResizeSize] = useState<{ w: number; h: number } | null>(
    null,
  );
  const [isResizing, setIsResizing] = useState(false);
  const resizeStart = useRef<{
    mouseX: number;
    mouseY: number;
    startW: number;
    startH: number;
  } | null>(null);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  useEffect(() => {
    if (open !== undefined) {
      setVisible(open);
      if (open) {
        setDragPos(null);
        setResizeSize(null);
      }
    }
  }, [open]);

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
    if (open === undefined) {
      setVisible(false);
    }
  };

  const handleMaskClick = (e: React.MouseEvent) => {
    if (maskClosable && e.target === e.currentTarget) {
      handleCancel();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (keyboard && e.key === 'Escape') {
      handleCancel();
    }
  };

  // ─── Drag logic ──────────────────────────────────────────────────────────
  const handleHeaderMouseDown = (e: React.MouseEvent) => {
    if (!draggable) {
      return;
    }
    e.preventDefault();

    const rect = modalRef.current?.getBoundingClientRect();
    if (!rect) {
      return;
    }

    dragStart.current = {
      mouseX: e.clientX,
      mouseY: e.clientY,
      boxX: rect.left,
      boxY: rect.top,
    };
    setIsDragging(true);

    const onMouseMove = (ev: MouseEvent) => {
      if (!dragStart.current) {
        return;
      }
      const dx = ev.clientX - dragStart.current.mouseX;
      const dy = ev.clientY - dragStart.current.mouseY;
      setDragPos({
        x: dragStart.current.boxX + dx,
        y: dragStart.current.boxY + dy,
      });
      if (onDrag) {
        onDrag();
      }
    };

    const onMouseUp = () => {
      dragStart.current = null;
      setIsDragging(false);
      if (onDragStop) {
        onDragStop();
      }
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };

  // ─── Resize logic (same pattern as drag – works everywhere) ──────────────
  const handleResizeMouseDown = (e: React.MouseEvent) => {
    if (!resizable) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();

    const rect = modalRef.current?.getBoundingClientRect();
    if (!rect) {
      return;
    }

    resizeStart.current = {
      mouseX: e.clientX,
      mouseY: e.clientY,
      startW: rect.width,
      startH: rect.height,
    };
    setIsResizing(true);

    const onMouseMove = (ev: MouseEvent) => {
      if (!resizeStart.current) {
        return;
      }
      const dw = ev.clientX - resizeStart.current.mouseX;
      const dh = ev.clientY - resizeStart.current.mouseY;
      setResizeSize({
        w: Math.max(320, resizeStart.current.startW + dw),
        h: Math.max(120, resizeStart.current.startH + dh),
      });
    };

    const onMouseUp = () => {
      resizeStart.current = null;
      setIsResizing(false);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };

  // ─── Inline styles ───────────────────────────────────────────────────────
  const modalStyle: React.CSSProperties = {
    ...style,
    ...(width ? { width } : {}),
    ...(height ? { height } : {}),
    // JS resize overrides CSS auto-sizing
    ...(resizeSize ? { width: resizeSize.w, height: resizeSize.h } : {}),
    // When dragged: fixed position at exact coordinates
    ...(dragPos
      ? {
          position: 'fixed',
          left: dragPos.x,
          top: dragPos.y,
          transform: 'none',
          margin: 0,
        }
      : {}),
    cursor: isDragging ? 'grabbing' : isResizing ? 'nwse-resize' : undefined,
  };

  const modalContent = (
    <>
      {mask && (
        <div
          className="modal__mask"
          style={{ zIndex }}
          onClick={handleMaskClick}
        />
      )}
      <div
        className={`modal__wrapper ${wrapClassName} ${centered && !dragPos ? 'modal__wrapper--centered' : ''}`}
        style={{ zIndex: zIndex + 1 }}
        onKeyDown={handleKeyDown}
        tabIndex={-1}
      >
        <div
          ref={modalRef}
          className={`modal__content ${className} ${draggable ? 'modal--draggable' : ''}`}
          style={modalStyle}
        >
          {/* Header – acts as drag handle */}
          <div
            className={`modal__header${!title ? ' modal__header--no-title' : ''}`}
            onMouseDown={handleHeaderMouseDown}
          >
            {title && <div className="modal__header-title">{title}</div>}
            {closable && (
              <button
                type="button"
                className="modal__header-close"
                onClick={handleCancel}
              >
                {closeIcon}
              </button>
            )}
          </div>

          {/* Body – grows with content, scrolls when it hits max-height */}
          <div className="modal__body" style={bodyStyle}>
            {children}
          </div>

          {/* Footer – flex-shrink:0 → always visible */}
          {footer !== null && (
            <div className="modal__footer">
              {footer || (
                <>
                  <Button {...cancelButtonProps} onClick={handleCancel}>
                    {cancelText}
                  </Button>
                  <Button
                    {...okButtonProps}
                    type={okType}
                    onClick={onOk}
                    loading={confirmLoading}
                  >
                    {okText}
                  </Button>
                </>
              )}
            </div>
          )}

          {/* JS-based resize handle */}
          {resizable && (
            <div
              className="modal__resize-handle"
              onMouseDown={handleResizeMouseDown}
            />
          )}
        </div>
      </div>
    </>
  );

  if (!mounted) {
    return null;
  }
  if (!visible && !forceRender) {
    return null;
  }

  const container =
    typeof getContainer === 'function' ? getContainer() : getContainer;
  if (container === false) {
    return modalContent;
  }

  return createPortal(modalContent, container as HTMLElement);
};

export default Modal;
